/**
 * ReportsNotesTabView - Displays the Reports & Notes tab content
 *
 * Contains AI scouting summary at the top, with a two-column layout
 * below showing scouting reports (left) and player notes (right).
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import AIScoutingSummaryCard from "@/features/player-detail/components/organisms/AIScoutingSummaryCard";
import NotesSection from "@/features/player-detail/components/organisms/NotesSection";
import ScoutingReportsList from "@/features/player-detail/components/organisms/ScoutingReportsList";
import { PlayerMatchReportsSection } from "@/features/player-match-reports";

import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";
import { Stack } from "@/components/atoms/Stack";

/**
 * Props for ReportsNotesTabView component
 */
interface ReportsNotesTabViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Player data fragment */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  /** Player display name for report PDF/header */
  readonly playerName?: string;
  /** Whether the layout should be desktop (two-column) or mobile (stacked) */
  readonly isDesktop: boolean;
}

/**
 * ReportsNotesTabView renders the Reports & Notes tab with AI summary
 * and two-column layout for reports and notes
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.player - Player data fragment
 * @param props.playerName - Player display name for report PDF/header
 * @param props.isDesktop - Whether to use desktop layout
 * @returns VStack containing the Reports & Notes tab layout
 */
const ReportsNotesTabView = ({
  isDark: _isDark,
  player,
  playerName,
  isDesktop,
}: ReportsNotesTabViewProps) => (
  <Stack className="w-full gap-4 py-4">
    {/* AI Scouting Summary at the top */}
    <AIScoutingSummaryCard player={player} />

    {/* Two-column layout for reports and notes */}
    {isDesktop ? (
      <Stack direction="horizontal" className="w-full gap-4">
        <ScoutingReportsList
          scoutReports={player?.scoutReports}
          playerId={player?.id}
          playerName={playerName}
          isSideBySide
        />
        <NotesSection playerId={player?.id} isSideBySide />
      </Stack>
    ) : (
      // SE-6910: `isSideBySide` is deliberately absent here. Each card takes a
      // zero flex basis only when it is sharing a ROW; in this stacked layout
      // the main axis is vertical, and a zero basis makes a card report a
      // height its own content does not fit inside — the notes list then paints
      // below the box without lengthening the scroll extent, leaving the last
      // note's controls underneath the floating action button with no scrolling
      // left to move them.
      <Stack className="w-full gap-4">
        <ScoutingReportsList
          scoutReports={player?.scoutReports}
          playerId={player?.id}
          playerName={playerName}
        />
        <NotesSection playerId={player?.id} />
      </Stack>
    )}

    {/*
      The player's MATCH reports (SE-7970), LAST on the tab (SE-8119), and
      ALWAYS rendered (SE-8149).

      Position: it opened the tab until SE-8119 moved it here, below Scouting
      Reports and Notes. One mount serves both layouts — it sits outside the
      desktop/mobile branch above, so "after them" holds at every width without
      the block being written twice.

      Always rendered: this mount passed `hideWhenEmpty` until SE-8149, so a
      player with no match reports got no section — on the full page and in the
      search preview modal alike, since the modal renders this very component
      tree. The reported symptom was the modal, and nothing about the modal was
      wrong: the section correctly hid itself for a player whose new-format
      count is zero, on a surface where the reader had ten legacy rows in front
      of them captioned "Match Report" and reasonably expected the section.

      It now behaves like every neighbour on the tab. Scouting Reports renders
      "No scouting reports yet" at zero and Notes renders its own zero state;
      Match reports was the only one that vanished, which reads as a rendering
      fault rather than as a scouting fact.

      Why the section exists here at all: this tab used to render
      `ScoutingReportsList` alone, which lists the legacy single-player scout
      reports — so a player with eleven match reports read "Scouting Reports 0 /
      No scouting reports yet" here, while `/players/[id]/reports` showed all
      eleven. Measured on dev: Kevin Yakob, 11 matches watched on the reports
      route, zero on this tab, which issued no match-report query at all. A
      counter reading zero where the reader has evidence to the contrary invites
      them to file the report a second time.

      The existing section is reused rather than a count being bolted onto the
      scout-reports header: the two are different artefacts with different
      shapes, and a merged number would make the list disagree with its own
      heading. `hasScoutReports` is false because the section's "Scout reports"
      caption introduces a list rendered directly beneath it, which on this tab
      is a separate card rather than the next row.
    */}
    <PlayerMatchReportsSection
      playerId={player?.id}
      hasScoutReports={false}
      playerName={playerName}
    />
  </Stack>
);

ReportsNotesTabView.displayName = "ReportsNotesTabView";

export default memo(ReportsNotesTabView);
